import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { API } from "@/App";
import { CheckCircle2, Loader2, Save, ArrowRight } from "lucide-react";

export default function EmailPreferencesPage() {
  const [params] = useSearchParams();
  const emailParam = params.get("email") || "";

  const [email, setEmail] = useState(emailParam);
  const [categories, setCategories] = useState([]);
  const [optOut, setOptOut] = useState([]);
  const [unsubAll, setUnsubAll] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");

  const load = async (addr) => {
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(addr)) return;
    setLoading(true);
    setError("");
    try {
      const r = await fetch(`${API}/marketing/preferences?email=${encodeURIComponent(addr)}`);
      const body = await r.json();
      if (!r.ok) throw new Error(body?.detail || "Load failed");
      setCategories(body.categories || []);
      setOptOut(body.categories_optout || []);
      setUnsubAll(!!body.unsubscribed_all);
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (emailParam) load(emailParam);
  }, [emailParam]);

  const toggleCategory = (key) => {
    setSaved(false);
    setOptOut((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  };

  const save = async () => {
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError("Enter a valid email address first.");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const r = await fetch(`${API}/marketing/preferences`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email, categories_optout: optOut, unsubscribed_all: unsubAll }),
      });
      if (!r.ok) {
        const body = await r.json().catch(() => ({}));
        throw new Error(body?.detail || "Save failed");
      }
      setSaved(true);
      setTimeout(() => setSaved(false), 3500);
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-100 flex items-center justify-center px-4 py-12" data-testid="prefs-page">
      <div className="w-full max-w-2xl bg-white rounded-2xl shadow-md border border-slate-200 overflow-hidden">
        <div className="bg-slate-900 px-6 py-5 text-white">
          <p className="text-[10px] font-bold uppercase tracking-[2px] text-amber-400 mb-1">Sterile Mate LLC · Email preferences</p>
          <h1 className="text-xl font-black">Choose what you hear about.</h1>
          <p className="text-sm text-slate-300 mt-2 leading-relaxed">
            Pick the topics you want. Uncheck what you don&apos;t. Order confirmations, RFQ replies, and quote responses will keep coming — those aren&apos;t marketing.
          </p>
        </div>

        <div className="p-6 space-y-5">
          <div>
            <label className="block text-xs font-bold uppercase tracking-wide text-slate-500 mb-1.5">Your email</label>
            <div className="flex gap-2">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onBlur={(e) => load(e.target.value)}
                placeholder="you@yourfacility.com"
                required
                data-testid="prefs-email-input"
                className="flex-1 px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-slate-900"
              />
              <button
                onClick={() => load(email)}
                type="button"
                data-testid="prefs-load-btn"
                className="px-4 py-2 bg-slate-100 border border-slate-300 hover:bg-slate-200 text-sm font-semibold rounded-lg transition"
              >
                Load
              </button>
            </div>
          </div>

          {error && (
            <div className="p-3 bg-rose-50 border border-rose-200 rounded-lg text-sm text-rose-800" data-testid="prefs-error">
              {error}
            </div>
          )}

          {categories.length > 0 && (
            <>
              <div className="pt-3 border-t border-slate-200">
                <p className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-3">Topics I DO NOT want to receive</p>
                <div className="space-y-2">
                  {categories.map((c) => {
                    const isOptedOut = optOut.includes(c.key);
                    return (
                      <label
                        key={c.key}
                        data-testid={`prefs-cat-${c.key}`}
                        className={`flex items-start gap-3 p-3 rounded-lg border cursor-pointer transition ${
                          isOptedOut ? "bg-rose-50 border-rose-200" : "bg-white border-slate-200 hover:border-slate-400"
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={isOptedOut}
                          onChange={() => toggleCategory(c.key)}
                          disabled={unsubAll}
                          className="mt-1 w-4 h-4"
                        />
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm font-bold ${isOptedOut ? "text-rose-900 line-through" : "text-slate-900"}`}>{c.label}</p>
                          <p className="text-xs text-slate-500 mt-0.5">{c.blurb}</p>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>

              <div className="pt-3 border-t border-slate-200">
                <label className="flex items-start gap-3 p-3 rounded-lg border border-slate-200 cursor-pointer" data-testid="prefs-unsub-all">
                  <input
                    type="checkbox"
                    checked={unsubAll}
                    onChange={(e) => {
                      setUnsubAll(e.target.checked);
                      setSaved(false);
                    }}
                    className="mt-1 w-4 h-4"
                  />
                  <div>
                    <p className="text-sm font-bold text-slate-900">Unsubscribe from ALL marketing emails</p>
                    <p className="text-xs text-slate-500 mt-0.5">Overrides the topic selections above.</p>
                  </div>
                </label>
              </div>

              <div className="pt-3 flex flex-wrap items-center justify-between gap-3 border-t border-slate-200">
                <div className="flex items-center gap-2 text-emerald-700 text-sm font-semibold">
                  {saved && (
                    <>
                      <CheckCircle2 className="w-4 h-4" />
                      Preferences saved.
                    </>
                  )}
                </div>
                <button
                  onClick={save}
                  disabled={loading}
                  data-testid="prefs-save-btn"
                  className="inline-flex items-center gap-2 px-5 py-2.5 bg-slate-900 hover:bg-slate-800 disabled:bg-slate-400 text-white text-sm font-bold rounded-lg transition"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                  Save preferences
                </button>
              </div>
            </>
          )}

          {loading && categories.length === 0 && (
            <div className="flex items-center gap-2 text-slate-600 text-sm">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading your preferences…
            </div>
          )}

          {!emailParam && categories.length === 0 && !loading && (
            <p className="text-xs text-slate-500">Enter your email above to load your current preferences.</p>
          )}

          <div className="pt-3 border-t border-slate-200">
            <Link
              to={`/unsubscribe${email ? `?email=${encodeURIComponent(email)}` : ""}`}
              data-testid="prefs-unsub-link"
              className="inline-flex items-center gap-1.5 text-xs text-slate-600 hover:text-slate-900 font-semibold"
            >
              Or unsubscribe from everything in one click <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>

        <div className="bg-slate-50 border-t border-slate-200 px-6 py-4 text-xs text-slate-500 leading-relaxed">
          Sterile Mate LLC · 396 W Greens Road, Suite 1139 · Houston, TX 77067 · USA · <a href="tel:8009883892" className="underline">800.988.3892</a>
        </div>
      </div>
    </div>
  );
}
